import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { concatLatestFrom } from '@ngrx/effects';
import { Subject, concat, filter, first, interval, mergeMap, pipe, takeUntil, tap } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CheckForUpdateComponent } from './check-for-update.component';
import { CustomComponentStore } from '@elio/shared/ngrx-utils';
import { LoggerService } from '@elio/shared/tools/logger';

const CHECK_FOR_UPDATE_INTERVAL_MS = 1000 * 60 * 5;
const ASK_LATER_DELAY_MS = 1000 * 60 * 10;

type VersionsStatus =
  | 'DELAY'
  | 'VERSION_PREPARED'
  | 'VERSION_DETECTED'
  | 'VERSION_INSTALLATION_FAILED'
  | 'VERSION_READY'
  | 'NO_NEW_VERSION_DETECTED'
  | 'LOADING';

interface CheckForUpdateState {
  versionStatus: VersionsStatus;
}

@Injectable({
  providedIn: 'root',
})
export class CheckForUpdateStore extends CustomComponentStore<CheckForUpdateState> {
  constructor(
    private swUpdate: SwUpdate,
    private loggerService: LoggerService,
    private appRef: ApplicationRef,
    private router: Router,
    public dialog: MatDialog
  ) {
    super({
      versionStatus: 'LOADING',
    });
  }

  readonly getVersionStatus$ = this.select((state) => state.versionStatus);
  readonly isPreparedVersion$ = this.select((state) => state.versionStatus === 'VERSION_PREPARED');

  private delayDestroy = new Subject<void>();

  readonly applyNewVersion = this.effect<void>(
    pipe(
      mergeMap(() =>
        this.getVersionStatus$.pipe(
          tap(() => {
            document.location.reload();
          })
        )
      )
    )
  );

  startDelay() {
    this.dialog.closeAll();
    this.patchStateVersionStatus('DELAY');
    interval(ASK_LATER_DELAY_MS)
      .pipe(takeUntil(this.delayDestroy))
      .subscribe(() => this.patchStateVersionStatus('VERSION_PREPARED'));
  }

  init() {
    this.loggerService.log('initializing CheckForUpdateStore', this.swUpdate.isEnabled);
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(
          concatLatestFrom(() => this.getVersionStatus$),
          filter(([, status]) => status !== 'VERSION_PREPARED'),
          tap(([evt]) => this.patchStateVersionStatus(evt.type)),
          takeUntil(this.destroy$)
        )
        .subscribe(([evt]) => {
          switch (evt.type) {
            case 'VERSION_DETECTED':
              this.loggerService.log(`Downloading new app version: ${evt.version.hash}`);
              break;
            case 'VERSION_READY':
              this.patchStateVersionStatus('VERSION_PREPARED');
              this.loggerService.log(`Current app version: ${evt.currentVersion.hash}`);
              this.loggerService.log(`New app version ready for use: ${evt.latestVersion.hash}`);
              break;
            case 'VERSION_INSTALLATION_FAILED':
              this.loggerService.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
              break;
          }
        });

      const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));

      const everyIntervalOrAppIsStable$ = concat(appIsStable$, interval(CHECK_FOR_UPDATE_INTERVAL_MS));

      everyIntervalOrAppIsStable$.pipe(takeUntil(this.destroy$)).subscribe(() =>
        this.swUpdate
          .checkForUpdate()
          .then((updateFound) =>
            this.loggerService.log(updateFound ? 'A new version is available. ' : 'Already on the latest version.')
          )
          .catch((err) => this.loggerService.error('Failed to check for updates:', err))
      );
    }
  }

  private patchStateVersionStatus(versionStatus: VersionsStatus) {
    if (versionStatus === 'VERSION_PREPARED') {
      if (this.router.url !== '/login') {
        this.delayDestroy.next();
        this.dialog.open(CheckForUpdateComponent, { disableClose: true });
      } else {
        this.applyNewVersion();
      }
    }
    this.patchState((state) => ({
      ...state,
      versionStatus,
    }));
  }
}
